import { useCallback, useMemo, useRef, useState } from 'react';

import { v4 } from 'uuid';

import { useLegacyShippingServiceRecommendation } from './use-legacy-shipping-service-recommendation';
import { useFulfillmentPlannerContext } from '../context';
import { Segment } from '../../services/analytics-services/segment';
import { FedexService } from '../../enums/label-providers/fedex/fedex-service';
import { FedexWalletService } from '../../enums/label-providers/fedex/fedex-walleted-service';
import { redactPIIFromShippingConfiguration } from '../../services/redacted-information-service';
import { useAlternativeRatesShippingServiceRecommendation } from './use-alternative-rates-shipping-service-recommendation';
import { NumberFormattingService } from '../../services/number-formatting-service';
import { APPLY_FROM_CSW, SHOW_FROM_CSW } from '../../constants/analytics-events';
import { trackShowRecommendation, trackApplyRecommendation } from './rate-recommendations.api';
import { RATE_BROWSER, CUSTOMERS_CREATE_LABEL } from '../../constants/fulfillment-plan-ids';

export const SHOWED_UPS_EVENT_NAME = 'SHOWED_CHEAPER_UPS_RATE_TO_USPS';
export const APPLIED_UPS_EVENT_NAME = 'APPLIED_CHEAPER_UPS_RATE_TO_USPS';

const byoaToWalletServiceMap = {
  // FedEx BYOA to FedEx Wallet mappings
  [FedexService.Ground]: FedexWalletService.Ground,
  [FedexService.HomeDelivery]: FedexWalletService.HomeDelivery,
  [FedexService.ExpressSaver]: FedexWalletService.Economy,
  [FedexService.TwoDay]: FedexWalletService.TwoDay,
  [FedexService.StandardOvernight]: FedexWalletService.StandardOvernight,
  [FedexService.PriorityOvernight]: FedexWalletService.PriorityOvernight,
  [FedexService.InternationalEconomy]: FedexWalletService.InternationalEconomy,
  [FedexService.InternationalPriority]: FedexWalletService.InternationalPriority,
  [FedexService.ConnectPlus]: FedexWalletService.InternationalConnectPlus,
};

export const useShippingServiceRecommendation = (fulfillmentPlanId, setShouldShowComparison, alternativeRatesRecommendation) => {
  const [shippingService, setShippingService] = useState({});
  const [rateDifference, setRateDifference] = useState({});
  const [isComparingBYOAToWallet, setIsComparingBYOAToWallet] = useState(false);
  const trackingData = useRef({});

  const compareRates = useCallback(
    ({ userRatesTotalCost, comparedRatesTotalCost, comparedService, metadata }) => {
      const userTotal = userRatesTotalCost?.value;
      const comparedTotal = comparedRatesTotalCost?.value;

      if (userTotal > comparedTotal) {
        const rateDiffValue = NumberFormattingService.roundToPlace(userTotal - comparedTotal, 2);
        setRateDifference({
          value: rateDiffValue,
          code: userRatesTotalCost.code,
        });
        setShippingService(comparedService);
        setShouldShowComparison(true);

        const { sellerId, originalShippingConfiguration } = metadata;

        setIsComparingBYOAToWallet(byoaToWalletServiceMap[originalShippingConfiguration.serviceId] === `${comparedService.serviceId}`);
        const analyticsData = {
          eventId: v4(),
          fulfillmentPlanId,
          amountCheaperValue: rateDiffValue,
          amountCheaperCode: userRatesTotalCost.code,
          originalShippingConfiguration: redactPIIFromShippingConfiguration(originalShippingConfiguration),
          suggestedShippingServiceId: comparedService?.serviceId,
          action: SHOW_FROM_CSW,
        };
        Segment.trackEvent(SHOWED_UPS_EVENT_NAME, analyticsData, { sellerId });
        const recommendation = {
          fulfillmentPlanId,
          toServiceId: comparedService?.serviceId,
          toSellerLabelProviderId: comparedService?.sellerLabelProviderId,
          toCarrierId: comparedService?.carrierId,
          toInsuranceProvider: originalShippingConfiguration.insuranceProvider,
          recommendationType: 'Service',
          toTotalCost: comparedRatesTotalCost,
          actionLocationType: 'CSW',
        };
        if (fulfillmentPlanId !== RATE_BROWSER && fulfillmentPlanId !== CUSTOMERS_CREATE_LABEL) {
          trackShowRecommendation(recommendation);
        }
        trackingData.current = {
          analyticsData,
          sellerId,
          recommendation,
          originalShippingConfiguration,
          userRatesTotalCost,
        };
      }
    },
    [fulfillmentPlanId, setShouldShowComparison],
  );

  useAlternativeRatesShippingServiceRecommendation({
    fulfillmentPlanId,
    compareRates,
    setShouldShowComparison,
    alternativeRatesRecommendation,
  });

  useLegacyShippingServiceRecommendation({
    fulfillmentPlanId,
    compareRates,
    setShouldShowComparison,
  });

  const { onServiceChangeWithSideEffects } = useFulfillmentPlannerContext();

  const onApplyRec = useCallback(() => {
    if (trackingData.current) {
      const { analyticsData, sellerId, recommendation, originalShippingConfiguration, userRatesTotalCost } = trackingData.current;

      const sendAnalytics = () => {
        trackApplyRecommendation({
          ...recommendation,
          fromServiceId: recommendation.toServiceId,
          fromSellerLabelProviderId: recommendation.toSellerLabelProviderId,
          fromCarrierId: recommendation.toCarrierId,
          fromInsuranceProvider: originalShippingConfiguration.insuranceProvider,
          fromTotalCost: userRatesTotalCost,
          fromShippingConfigurationHash: originalShippingConfiguration.shippingConfigurationHash,
        });
      };

      onServiceChangeWithSideEffects({
        serviceId: shippingService.serviceId,
        sellerLabelProviderId: shippingService.sellerLabelProviderId,
        sendAnalytics,
      });
      Segment.trackEvent(
        APPLIED_UPS_EVENT_NAME,
        {
          ...analyticsData,
          action: APPLY_FROM_CSW,
        },
        { sellerId },
      );
    }
  }, [onServiceChangeWithSideEffects, shippingService.serviceId, shippingService.sellerLabelProviderId]);

  return useMemo(
    () => ({
      rateDifference,
      onApplyRec,
      shippingService,
      isComparingBYOAToWallet,
    }),
    [rateDifference, onApplyRec, shippingService, isComparingBYOAToWallet],
  );
};
